import { LitElement, html, css } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { authService } from '../../services/auth/index.js';
import { getVersionedApiUrl } from '../../config/api-config.js';

// Dynamic imports for enhanced features (using local packages)
let hljs = null;

class MarkdownElement extends LitElement {
  static get properties() {
    return {
      markdown: { type: String },
      enableSyntaxHighlighting: { type: Boolean },
      enableCopyButtons: { type: Boolean },
      theme: { type: String },
      autoFormatCode: { type: Boolean }, // Off by default to preserve markdown as-is
      allowDataUrls: { type: Boolean }, // Security: Allow data: URLs if explicitly needed
      allowStyles: { type: Boolean }, // Security: Allow inline styles if explicitly needed
      _processingComplete: { type: Number } // Internal property to trigger re-renders
    };
  }

  static get styles() {
    return css`
      :host {
        /* Modern font stack similar to Claude - clean and sophisticated */
        --font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
        --font-mono: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Menlo, Consolas, "Liberation Mono", "Courier New", monospace;
        
        /* Enhanced typography scale */
        --text-xs: 0.75rem;     /* 12px */
        --text-sm: 0.875rem;    /* 14px */
        --text-base: 1rem;      /* 16px */
        --text-lg: 1.125rem;    /* 18px */
        --text-xl: 1.25rem;     /* 20px */
        --text-2xl: 1.5rem;     /* 24px */
        --text-3xl: 1.875rem;   /* 30px */
        --text-4xl: 2.25rem;    /* 36px */

        /* Font weights */
        --font-normal: 400;
        --font-medium: 500;
        --font-semibold: 600;
        --font-bold: 700;

        /* Line heights for better readability */
        --leading-tight: 1.25;
        --leading-normal: 1.5;
        --leading-relaxed: 1.625;

        /* Spacing system */
        --spacing-xs: 0.5rem;
        --spacing-sm: 0.75rem;
        --spacing-md: 1rem;
        --spacing-lg: 1.5rem;
        --spacing-xl: 2rem;

        /* Theme colors */
        --color-primary: #2563eb;
        --color-primary-hover: #1d4ed8;
        --color-success: #10b981;
        --color-warning: #f59e0b;
        --color-error: #ef4444;
        --color-muted: #6b7280;

        display: block;
        max-width: 100%;
        min-width: 0;
        overflow-wrap: break-word;
        word-wrap: break-word;
        font-family: var(--font-family);
        font-size: var(--text-base);
        font-weight: var(--font-normal);
        line-height: var(--leading-normal);
        color: #000000;
        background: none;
        font-feature-settings: normal;
        font-variation-settings: normal;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      /* Enhanced heading hierarchy */
      h1 {
        font-size: var(--text-3xl);
        font-weight: var(--font-semibold);
        line-height: var(--leading-tight);
        margin: var(--spacing-xl) 0 var(--spacing-lg);
        color: #111111;
        letter-spacing: -0.025em;
        border-bottom: 2px solid #e5e7eb;
        padding-bottom: var(--spacing-sm);
      }

      h2 {
        font-size: var(--text-2xl);
        font-weight: var(--font-semibold);
        line-height: var(--leading-tight);
        margin: var(--spacing-lg) 0 var(--spacing-md);
        color: #111111;
        letter-spacing: -0.025em;
        border-bottom: 1px solid #f3f4f6;
        padding-bottom: var(--spacing-xs);
      }

      h3 {
        font-size: var(--text-xl);
        font-weight: var(--font-medium);
        line-height: var(--leading-tight);
        margin: var(--spacing-lg) 0 var(--spacing-sm);
        color: #111111;
      }

      h4, h5, h6 {
        font-size: var(--text-lg);
        font-weight: var(--font-medium);
        line-height: var(--leading-tight);
        margin: var(--spacing-md) 0 var(--spacing-sm);
        color: #111111;
      }

      /* Enhanced paragraph styling */
      p {
        font-size: var(--text-base);
        line-height: var(--leading-relaxed);
        margin: var(--spacing-md) 0;
        color: #374151;
      }

      /* Enhanced text styling */
      strong, b {
        font-weight: var(--font-semibold);
        color: #111111;
      }

      em, i {
        font-style: italic;
        color: #4b5563;
      }

      mark {
        background: #fef3c7;
        color: #92400e;
        padding: 0.125em 0.25em;
        border-radius: 0.25rem;
      }

      /* Professional lists */
      ul, ol {
        margin: var(--spacing-md) 0;
        padding-left: var(--spacing-lg);
        line-height: var(--leading-relaxed);
        color: #374151;
      }

      li {
        margin: 0.375rem 0;
        padding-left: 0.25rem;
      }

      /* Task lists */
      li input[type="checkbox"] {
        margin-right: 0.5rem;
        accent-color: var(--color-primary);
      }

      /* CODE BLOCKS WITH SYNTAX HIGHLIGHTING */
      .code-block-container {
        position: relative;
        margin: var(--spacing-lg) 0;
        border-radius: 0.5rem;
        overflow: hidden;
        border: 1px solid #e5e7eb;
        background: #f9fafb;
        max-width: 100%;
        min-width: 0; /* Allows flex shrinking */
      }

      .code-block-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem var(--spacing-md);
        background: #f3f4f6;
        border-bottom: 1px solid #e5e7eb;
        font-size: var(--text-sm);
        color: var(--color-muted);
      }

      .code-language {
        font-family: var(--font-mono);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .copy-button {
        padding: 0.25rem 0.5rem;
        font-size: var(--text-xs);
        background: var(--color-primary);
        color: white;
        border: none;
        border-radius: 0.25rem;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: var(--font-mono);
        font-weight: var(--font-medium);
        min-width: 60px;
        text-align: center;
      }

      .copy-button:hover:not(:disabled) {
        background: var(--color-primary-hover);
        transform: translateY(-1px);
      }

      .copy-button:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }

      .copy-button.copied {
        background: var(--color-success);
        transform: none;
      }

      .copy-button:active {
        transform: translateY(0);
      }

      pre {
        margin: 0;
        padding: var(--spacing-md);
        overflow-x: auto;
        overflow-y: hidden;
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        line-height: 1.5;
        background: #ffffff;
        max-width: 100%;
        min-width: 0;
        white-space: pre;
        word-wrap: normal;
        word-break: normal;
      }

      pre code {
        background: none;
        border: none;
        padding: 0;
        color: inherit;
        font-weight: 400;
        white-space: pre;
        display: block;
        overflow-x: auto;
        max-width: 100%;
      }

      /* Inline code */
      :not(pre) > code {
        font-family: var(--font-mono);
        font-size: 0.875em;
        background: #f3f4f6;
        color: #111827;
        padding: 0.125em 0.375em;
        border-radius: 0.25rem;
        border: 1px solid #e5e7eb;
        font-weight: 500;
        word-break: break-all;
        overflow-wrap: break-word;
        white-space: pre-wrap;
      }

      /* JSON rendering */
      .json-container {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 0.5rem;
        margin: var(--spacing-md) 0;
        overflow: hidden;
        max-width: 100%;
        min-width: 0;
      }

      .json-header {
        background: #e2e8f0;
        padding: 0.5rem var(--spacing-md);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: #475569;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .json-content {
        padding: var(--spacing-md);
        overflow-x: auto;
        overflow-y: hidden;
        max-width: 100%;
      }

      .json-viewer {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        line-height: 1.4;
        white-space: pre;
        word-wrap: normal;
        word-break: normal;
        max-width: 100%;
      }


      /* Enhanced blockquotes */
      blockquote {
        margin: var(--spacing-lg) 0;
        padding: var(--spacing-md) var(--spacing-lg);
        background: #f9fafb;
        border-left: 4px solid var(--color-primary);
        border-radius: 0 0.5rem 0.5rem 0;
        font-style: italic;
        font-size: var(--text-base);
        line-height: var(--leading-relaxed);
        color: #4b5563;
        position: relative;
      }

      blockquote::before {
        content: '"';
        font-size: 4rem;
        color: var(--color-primary);
        position: absolute;
        top: -0.5rem;
        left: 1rem;
        opacity: 0.3;
        font-family: serif;
      }

      /* Enhanced links */
      a {
        color: var(--color-primary);
        text-decoration: none;
        font-weight: var(--font-medium);
        border-bottom: 1px solid transparent;
        transition: all 0.15s ease;
        position: relative;
      }

      a:hover {
        color: var(--color-primary-hover);
        border-bottom-color: var(--color-primary-hover);
      }

      /* External link indicator */
      a[href^="http"]:not([href*="kablewy.ai"])::after {
        content: "↗";
        font-size: 0.75em;
        margin-left: 0.25em;
        opacity: 0.6;
      }

      /* Enhanced tables */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--text-sm);
        font-family: var(--font-family);
        margin: var(--spacing-lg) 0;
        border-radius: 0.5rem;
        border: 1px solid #e5e7eb;
        background: #ffffff;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
      }

      th {
        padding: var(--spacing-md);
        background: #f9fafb;
        color: #111827;
        text-align: left;
        font-weight: var(--font-semibold);
        letter-spacing: 0.025em;
        border-bottom: 1px solid #e5e7eb;
        position: sticky;
        top: 0;
        z-index: 1;
      }

      td {
        padding: var(--spacing-md);
        border-bottom: 1px solid #f3f4f6;
        text-align: left;
        vertical-align: top;
        color: #374151;
        background: #ffffff;
      }

      tbody tr:nth-of-type(even) td {
        background: #f9fafb;
      }

      tbody tr:hover td {
        background: #f3f4f6;
      }

      /* Images with captions */
      .image-container {
        margin: var(--spacing-lg) 0;
        text-align: center;
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 0.5rem;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
      }

      .image-caption {
        margin-top: var(--spacing-sm);
        font-size: var(--text-sm);
        color: var(--color-muted);
        font-style: italic;
      }

      /* Horizontal rules */
      hr {
        border: none;
        height: 2px;
        background: linear-gradient(to right, transparent, #e5e7eb, transparent);
        margin: var(--spacing-xl) 0;
      }

      /* Keyboard shortcuts */
      kbd {
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        border-radius: 0.25rem;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        color: #374151;
        font-family: var(--font-mono);
        font-size: 0.875em;
        font-weight: var(--font-medium);
        padding: 0.125rem 0.375rem;
        white-space: nowrap;
      }

      /* Processing state */
      .markdown-loading {
        padding: var(--spacing-md);
        text-align: center;
        color: var(--color-muted);
        font-style: italic;
        background: #f9fafb;
        border-radius: 0.5rem;
        border: 1px solid #e5e7eb;
        animation: pulse 1.5s ease-in-out infinite;
      }

      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.7; }
      }

      /* Responsive design */
      @media (max-width: 768px) {
        :host {
          --text-4xl: 2rem;
          --text-3xl: 1.75rem;
          --text-2xl: 1.375rem;
        }
        
        table {
          font-size: var(--text-xs);
        }
        
        th, td {
          padding: var(--spacing-sm);
        }

        .code-block-container {
          margin: var(--spacing-md) 0;
        }

        blockquote {
          margin-left: 0;
          padding: var(--spacing-sm) var(--spacing-md);
        }

        pre {
          padding: var(--spacing-sm);
          border-radius: 0.25rem;
        }
      }

      /* Dark mode support */
      @media (prefers-color-scheme: dark) {
        :host([theme="auto"]) {
          --color-primary: #60a5fa;
          --color-primary-hover: #3b82f6;
          color: #f9fafb;
        }

        :host([theme="auto"]) h1,
        :host([theme="auto"]) h2,
        :host([theme="auto"]) h3,
        :host([theme="auto"]) h4,
        :host([theme="auto"]) h5,
        :host([theme="auto"]) h6 {
          color: #f9fafb;
        }

        :host([theme="auto"]) .code-block-container {
          background: #1f2937;
          border-color: #374151;
        }

        :host([theme="auto"]) .code-block-header {
          background: #374151;
          border-color: #4b5563;
        }

        :host([theme="auto"]) pre {
          background: #111827;
        }
      }

      /* Accessibility improvements */
      @media (prefers-reduced-motion: reduce) {
        * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }

      /* Focus states */
      a:focus,
      button:focus {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
        border-radius: 0.25rem;
      }

      /* Text selection */
      ::selection {
        background: rgba(37, 99, 235, 0.15);
        color: #111827;
      }
    `;
  }

  constructor() {
    super();
    this.markdown = "";
    this.enableSyntaxHighlighting = true;
    this.enableCopyButtons = true;
    this.theme = "auto";
    this.autoFormatCode = false; // Off by default - preserve markdown as-is
    this.allowDataUrls = false; // Security: Off by default
    this.allowStyles = false; // Security: Off by default
    
    this._loadedLibraries = {
      highlight: false
    };
    
    this._processedContent = "";
    this._isProcessing = false;
    this._lastProcessedMarkdown = "";
    this._processingComplete = 0; // Timestamp to trigger re-renders
    
    // Set default marked options once to ensure consistency
    this._setupMarkedDefaults();
  }

  /**
   * Setup default marked options for consistent rendering
   */
  _setupMarkedDefaults() {
    marked.setOptions({
      breaks: true,        // Preserve line breaks
      gfm: true,          // GitHub Flavored Markdown
      tables: true,       // Support tables
      sanitize: false,    // We sanitize with DOMPurify instead
      smartLists: true,   // Better list handling
      smartypants: true,  // Smart quotes and dashes
      langPrefix: 'hljs language-'
    });
  }

  /**
   * Load external libraries dynamically
   */
  async _loadLibraries() {
    const promises = [];

    // Load Highlight.js for syntax highlighting
    if (this.enableSyntaxHighlighting && !this._loadedLibraries.highlight) {
      promises.push(this._loadHighlightJs());
    }

    await Promise.allSettled(promises);
  }

  async _loadHighlightJs() {
    try {
      if (!hljs) {
        hljs = await import('highlight.js');
        
        // Load CSS styles for syntax highlighting
        this._loadHighlightJsStyles();
        
        // Load common languages from local package
        await Promise.all([
          import('highlight.js/lib/languages/javascript'),
          import('highlight.js/lib/languages/typescript'),
          import('highlight.js/lib/languages/python'),
          import('highlight.js/lib/languages/json'),
          import('highlight.js/lib/languages/xml'), // html is now xml in newer versions
          import('highlight.js/lib/languages/css'),
          import('highlight.js/lib/languages/sql'),
          import('highlight.js/lib/languages/bash'),
          import('highlight.js/lib/languages/yaml'),
          import('highlight.js/lib/languages/markdown')
        ]).catch(() => {}); // Ignore language loading errors
      }
      this._loadedLibraries.highlight = true;
    } catch (error) {
      console.warn('[MarkdownElement] Failed to load Highlight.js:', error);
    }
  }

  /**
   * Load highlight.js CSS styles dynamically
   */
  _loadHighlightJsStyles() {
    // Check if highlight.js styles are already loaded
    if (document.querySelector('link[data-hljs-theme]')) {
      return;
    }
    
    // Create and inject highlight.js CSS with SRI for security
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github.min.css';
    // Security: Add Subresource Integrity (SRI) to prevent CDN tampering
    link.integrity = 'sha512-0aPQyyeZrWj9sCA46UlmWgKOP0mUipLQ6OZXu8l4IcAmD2u31EPEy9VcIMvl7SoAaKe8bLXZhYoMaE/in+gcgA==';
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-hljs-theme', 'github');
    document.head.appendChild(link);
    
    // Highlight.js CSS theme loaded
  }

  /**
   * Enhanced markdown processing with all features
   */
  async _processMarkdown(markdown) {
    if (!markdown) return '';
    
    // Security: Limit input size to prevent ReDoS attacks
    const MAX_MARKDOWN_LENGTH = 500000; // 500KB limit
    if (markdown.length > MAX_MARKDOWN_LENGTH) {
      console.warn('[MarkdownElement] Input too large, truncating for security');
      markdown = markdown.substring(0, MAX_MARKDOWN_LENGTH) + '\n\n... (content truncated for security)';
    }

    await this._loadLibraries();

    // Configure marked with enhanced options (keeping the defaults we set in constructor)
    marked.setOptions({
      ...marked.defaults,  // Preserve our default options
      highlight: this.enableSyntaxHighlighting && hljs ? (code, lang) => {
        if (lang && hljs.default.getLanguage(lang)) {
          try {
            return hljs.default.highlight(code, { language: lang }).value;
          } catch (err) {
            console.warn('[MarkdownElement] Syntax highlighting error:', err);
          }
        }
        return hljs.default.highlightAuto(code).value;
      } : null
    });

    let processedMarkdown = markdown;

    // Only auto-format code if explicitly enabled (off by default)
    if (this.autoFormatCode) {
      processedMarkdown = this._processInlineCode(processedMarkdown);
    }

    // Convert markdown to HTML
    let html = marked(processedMarkdown);
    // Sanitize rendered HTML to prevent XSS while preserving styling/classes
    try {
      // Build allowed attributes based on security settings
      const allowedAttrs = ['class', 'href', 'name', 'target', 'rel', 'src', 'alt', 'title', 'id'];
      if (this.allowStyles) {
        allowedAttrs.push('style'); // Only allow if explicitly enabled
      }
      
      // Build URI regex based on security settings
      const uriSchemes = this.allowDataUrls 
        ? /^(?:(?:https?|mailto|tel|data|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i
        : /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i;
      
      html = DOMPurify.sanitize(html, {
        ALLOWED_ATTR: allowedAttrs,
        ALLOWED_URI_REGEXP: uriSchemes,
        RETURN_TRUSTED_TYPE: false,
        // Additional security options
        FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
      });
    } catch {}

    // Post-process HTML for enhancements
    html = this._enhanceHtml(html);
    
    // Auto-refresh any R2 presigned URLs that might be expired
    html = await this._refreshImageUrls(html);

    return html;
  }

  /**
   * Process inline code (JSON, HTML, XML) for better formatting
   */
  _processInlineCode(markdown) {
    let processed = markdown;
    
    // 1. Format JSON that's already in code blocks but poorly formatted
    processed = processed.replace(/```json\n([\s\S]*?)\n```/g, (match, jsonContent) => {
      // Skip if it looks already formatted (has multiple lines with proper indentation)
      if (jsonContent.includes('\n  ')) {
        return match;
      }
      
      try {
        // Try to parse and format the JSON
        const parsed = JSON.parse(jsonContent.trim());
        const formatted = JSON.stringify(parsed, null, 2);
        return '```json\n' + formatted + '\n```';
      } catch (error) {
        // Not valid JSON or parsing error, leave it alone
        return match;
      }
    });
    
    // 2. Format inline JSON objects/arrays that are NOT inside code blocks
    // More conservative - only match complete JSON on its own line or clear JSON objects
    processed = processed.replace(/^(\s*)(\{[^\n]*\}|\[[^\n]*\])(\s*)$/gm, (match, before, json, after) => {
      // Skip if inside a code block (has backticks nearby)
      if (match.includes('`')) return match;
      
      try {
        // Try to parse it as JSON
        const parsed = JSON.parse(json);
        // Only format if it's a non-trivial object/array
        if ((typeof parsed === 'object' && Object.keys(parsed).length > 0) || 
            (Array.isArray(parsed) && parsed.length > 0)) {
          const formatted = JSON.stringify(parsed, null, 2);
          return before + '```json\n' + formatted + '\n```' + after;
        }
        return match;
      } catch (error) {
        // Not valid JSON, leave it alone
        return match;
      }
    });
    
    // 3. DISABLED: HTML processing was too aggressive and breaking valid markdown
    // Only wrap complete HTML documents, not fragments
    // const htmlDocRegex = /^<!DOCTYPE[^>]*>[\s\S]*<html[\s\S]*<\/html>/gmi;
    // processed = processed.replace(htmlDocRegex, (match) => {
    //   return '```html\n' + match + '\n```';
    // });
    
    return processed;
  }

  /**
   * Enhance HTML with additional features
   */
  _enhanceHtml(html) {
    if (this.enableCopyButtons) {
      html = this._addCopyButtonsToCodeBlocks(html);
    }
    
    // Don't wrap tables - let them flow naturally
    // html = this._enhanceTables(html);
    // Don't wrap images - can affect layout
    // html = this._enhanceImages(html);
    html = this._enhanceExternalLinks(html);
    
    return html;
  }

  /**
   * Add copy buttons to code blocks
   */
  _addCopyButtonsToCodeBlocks(html) {
    const timestamp = Date.now();
    let codeBlockCounter = 0;
    
    // Handle code blocks with language specification
    html = html.replace(
      /<pre><code class="(?:language-)?(\w+)">/g,
      (match, lang) => {
        const id = `code-${timestamp}-${codeBlockCounter++}`;
        return `
          <div class="code-block-container">
            <div class="code-block-header">
              <span class="code-language">${lang}</span>
              <button class="copy-button" data-copy-target="${id}" data-copy-type="code">Copy</button>
            </div>
            <pre><code class="language-${lang}" id="${id}">
        `;
      }
    );
    
    // Handle code blocks without language specification
    html = html.replace(
      /<pre><code>/g,
      (match) => {
        const id = `code-${timestamp}-${codeBlockCounter++}`;
        return `
          <div class="code-block-container">
            <div class="code-block-header">
              <span class="code-language">text</span>
              <button class="copy-button" data-copy-target="${id}" data-copy-type="code">Copy</button>
            </div>
            <pre><code id="${id}">
        `;
      }
    );
    
    return html.replace(/<\/code><\/pre>/g, '</code></pre></div>');
  }

  /**
   * Enhance tables with containers
   * NOTE: Disabled to preserve natural table flow - tables look better without wrapper constraints
   */
  // _enhanceTables(html) {
  //   return html
  //     .replace(/<table>/g, '<div class="table-container"><table>')
  //     .replace(/<\/table>/g, '</table></div>');
  // }

  /**
   * Enhance images with containers and captions
   * NOTE: Disabled to preserve natural image flow and prevent layout issues
   */
  // _enhanceImages(html) {
  //   return html.replace(
  //     /<img([^>]+)alt="([^"]*)"([^>]*)>/g,
  //     (match, before, alt, after) => {
  //       if (alt) {
  //         return `
  //           <div class="image-container">
  //             <img${before}alt="${alt}"${after}>
  //             <div class="image-caption">${alt}</div>
  //           </div>
  //         `;
  //       }
  //       return match;
  //     }
  //   );
  // }

  /**
   * Enhance external links to open in new tabs
   */
  _enhanceExternalLinks(html) {
    return html.replace(
      /<a href="(https?:\/\/(?!kablewy\.ai)[^"]+)"([^>]*)>/g,
      '<a href="$1"$2 target="_blank" rel="noopener noreferrer">'
    );
  }

  /**
   * Check if a URL is a presigned R2 URL that should be refreshed
   * @param {string} url - The URL to check
   * @returns {boolean}
   */
  _isR2PresignedUrl(url) {
    if (!url) return false;
    return url.includes('r2.cloudflarestorage.com') || 
           url.includes('X-Amz-Signature') ||
           url.includes('X-Amz-Credential');
  }

  /**
   * Extract image ID from a presigned R2 URL or Kablewy image URL
   * @param {string} url - The image URL
   * @returns {string|null} - The image ID or null if not found
   */
  _extractImageIdFromUrl(url) {
    if (!url) return null;
    
    // Match patterns like:
    // - /artifacts/{orgId}/{userId}/img_xxx.png
    // - /artifacts/{orgId}/{userId}/images/img_xxx.png  
    // - /images/{orgId}/users/{userId}/download/img_xxx
    // - img_xxx anywhere in the path
    const patterns = [
      /\/artifacts\/[^/]+\/[^/]+\/(?:images\/)?(img_[a-z0-9_]+)\./i,
      /\/download\/(img_[a-z0-9_]+)/i,
      /\/(img_\d+_[a-z0-9]+)\./i,
      // Generic pattern for any ID-like string before extension
      /\/([a-z]+_\d+_[a-z0-9]+)\./i
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  }

  /**
   * Get a fresh download URL for an image
   * @param {string} imageId - The image ID
   * @returns {Promise<string|null>} - Fresh URL or null
   */
  async _getFreshImageUrl(imageId) {
    if (!imageId) return null;
    
    try {
      const sessionData = await authService.getSessionData?.();
      const orgId = authService.getOrganizationId?.();
      const userId = sessionData?.userData?.id;
      
      if (!orgId || !userId) return null;
      
      // Use the images download endpoint which serves directly from R2 (no presigning needed)
      const apiUrl = getVersionedApiUrl();
      return `${apiUrl}/images/${orgId}/users/${userId}/download/${imageId}`;
    } catch (error) {
      console.warn('[MarkdownElement] Failed to generate fresh image URL:', error);
      return null;
    }
  }

  /**
   * Process HTML to refresh any R2 presigned URLs
   * This ensures images always load even if the original presigned URL has expired
   * @param {string} html - The HTML content
   * @returns {Promise<string>} - HTML with refreshed image URLs
   */
  async _refreshImageUrls(html) {
    if (!html) return html;
    
    // Find all img tags with src attributes
    const imgRegex = /<img([^>]*?)src="([^"]+)"([^>]*)>/gi;
    const matches = [...html.matchAll(imgRegex)];
    
    if (matches.length === 0) return html;
    
    let result = html;
    
    for (const match of matches) {
      const [fullMatch, before, src, after] = match;
      
      // Only refresh R2 presigned URLs (they have X-Amz-Signature params)
      if (this._isR2PresignedUrl(src)) {
        const imageId = this._extractImageIdFromUrl(src);
        if (imageId) {
          const freshUrl = await this._getFreshImageUrl(imageId);
          if (freshUrl) {
            // Replace the old presigned URL with the fresh download URL
            const newImgTag = `<img${before}src="${freshUrl}"${after}>`;
            result = result.replace(fullMatch, newImgTag);
            console.log('[MarkdownElement] Auto-refreshed R2 presigned URL for:', imageId);
          }
        }
      }
    }
    
    return result;
  }

  /**
   * Copy content to clipboard (unified method for code, JSON, etc.)
   */
  async _copyToClipboard(elementId, buttonElement, contentType = 'content') {
    try {
      const element = this.shadowRoot.getElementById(elementId);
      if (!element) {
        console.error('[MarkdownElement] Copy target element not found:', elementId);
        this._showCopyFeedback(buttonElement, 'Error!', false);
        return;
      }

      const textToCopy = element.textContent || element.innerText;
      if (!textToCopy) {
        console.error(`[MarkdownElement] No ${contentType} content to copy`);
        this._showCopyFeedback(buttonElement, 'No content!', false);
        return;
      }

      // Check if clipboard API is available and we have permission
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(textToCopy);
          console.log(`[MarkdownElement] Successfully copied ${contentType} to clipboard`);
          this._showCopyFeedback(buttonElement, 'Copied!', true);
          return;
        } catch (clipboardError) {
          console.warn('[MarkdownElement] Clipboard API failed, using fallback:', clipboardError);
        }
      }
      
      // Use fallback method
      this._fallbackCopy(elementId, buttonElement);
      
    } catch (error) {
      console.error(`[MarkdownElement] Failed to copy ${contentType}:`, error);
      this._showCopyFeedback(buttonElement, 'Failed!', false);
    }
  }

  /**
   * Show visual feedback for copy operation
   */
  _showCopyFeedback(buttonElement, message, isSuccess) {
    if (!buttonElement) return;

    const originalText = buttonElement.textContent;
    buttonElement.textContent = message;
    
    if (isSuccess) {
      buttonElement.classList.add('copied');
    } else {
      buttonElement.style.background = '#ef4444'; // Error color
    }
    
    setTimeout(() => {
      buttonElement.textContent = originalText;
      buttonElement.classList.remove('copied');
      buttonElement.style.background = ''; // Reset background
    }, 2000);
  }

  /**
   * Fallback copy method for browsers without clipboard API support
   */
  _fallbackCopy(elementId, buttonElement) {
    try {
      const element = this.shadowRoot.getElementById(elementId);
      if (!element) {
        this._showCopyFeedback(buttonElement, 'Error!', false);
        return;
      }

      // Create a temporary textarea
      const textarea = document.createElement('textarea');
      textarea.value = element.textContent || element.innerText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      
      if (successful) {
        console.log('[MarkdownElement] Fallback copy successful');
        this._showCopyFeedback(buttonElement, 'Copied!', true);
      } else {
        console.error('[MarkdownElement] Fallback copy failed');
        this._showCopyFeedback(buttonElement, 'Failed!', false);
      }
    } catch (error) {
      console.error('[MarkdownElement] Fallback copy error:', error);
      this._showCopyFeedback(buttonElement, 'Failed!', false);
    }
  }

  willUpdate(changedProperties) {
    super.willUpdate(changedProperties);
    
    // Only process markdown if it actually changed (not just during render cycles)
    if (changedProperties.has('markdown') && 
        !this._isProcessing && 
        this.markdown !== this._lastProcessedMarkdown) {
      
      // Processing markdown
      this._isProcessing = true;
      this._lastProcessedMarkdown = this.markdown;
      
      this._processMarkdown(this.markdown).then(content => {
        this._processedContent = content;
        this._isProcessing = false;
        this._processingComplete = Date.now(); // 🔧 FIX: Trigger re-render naturally via property change
        // Schedule event listener attachment after the DOM updates
        this.updateComplete.then(() => {
          this._addCopyEventListeners();
        });
      }).catch(error => {
        console.error('[MarkdownElement] Processing error:', error);
        // Fallback to basic marked processing
        try {
          let basic = marked(this.markdown || '');
          this._processedContent = DOMPurify.sanitize(basic, {
            ALLOWED_ATTR: ['class','href','name','target','rel','src','alt','title','id'],
            ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
            FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover']
          });
        } catch (fallbackError) {
          this._processedContent = DOMPurify.sanitize(this.markdown || '', {
            ALLOWED_ATTR: ['class','href','name','target','rel','src','alt','title','id'],
            ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
            FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
            FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover']
          });
        }
        this._isProcessing = false;
        this._processingComplete = Date.now(); // 🔧 FIX: Trigger re-render naturally via property change
        // Schedule event listener attachment after the DOM updates
        this.updateComplete.then(() => {
          this._addCopyEventListeners();
        });
      });
    }
  }

  async updated(changedProperties) {
    super.updated(changedProperties);
    
    // Only add event listeners for immediate render (when not processing asynchronously)
    if (changedProperties.has('markdown') && !this._isProcessing) {
      this._addCopyEventListeners();
    }
  }

  /**
   * Add copy button event listeners using event delegation
   */
  _addCopyEventListeners() {
    // Remove any existing delegated listener to prevent duplicates
    if (this._copyEventHandler) {
      this.shadowRoot.removeEventListener('click', this._copyEventHandler);
    }
    
    // Create single delegated event handler
    this._copyEventHandler = async (e) => {
      if (!e.target.classList.contains('copy-button')) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const targetId = e.target.getAttribute('data-copy-target');
      const copyType = e.target.getAttribute('data-copy-type');
      
      if (!targetId || !copyType) {
        console.error('[MarkdownElement] Missing copy attributes:', { targetId, copyType });
        this._showCopyFeedback(e.target, 'Error!', false);
        return;
      }

      // Disable button during copy operation
      e.target.disabled = true;
      
      try {
        await this._copyToClipboard(targetId, e.target, copyType);
      } catch (error) {
        console.error('[MarkdownElement] Copy operation failed:', error);
        this._showCopyFeedback(e.target, 'Failed!', false);
      } finally {
        // Re-enable button after feedback duration
        setTimeout(() => {
          e.target.disabled = false;
        }, 2100);
      }
    };
    
    // Attach single delegated listener
    this.shadowRoot.addEventListener('click', this._copyEventHandler);
    
    const copyButtons = this.shadowRoot.querySelectorAll('.copy-button');
    // Copy delegation ready for copy buttons
  }

  render() {
    // Return processed content if available, otherwise show loading or raw markdown
    if (this._isProcessing) {
      return html`<div class="markdown-loading">Processing markdown...</div>`;
    }
    
    // Render content directly without wrapper divs to preserve component styles
    if (this._processedContent) {
      return html`${unsafeHTML(this._processedContent)}`;
    } else if (this.markdown) {
      // Immediate basic processing for instant feedback while complex processing happens
      // Uses the same marked options we set in constructor for consistency
      try {
        const basicHtml = marked(this.markdown);
        const sanitized = DOMPurify.sanitize(basicHtml, {
          ALLOWED_ATTR: ['class','href','name','target','rel','src','alt','title','id'],
          ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i,
          RETURN_TRUSTED_TYPE: false,
          FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form'],
          FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover']
        });
        return html`${unsafeHTML(sanitized)}`;
      } catch (error) {
        console.warn('[MarkdownElement] Basic markdown processing failed:', error);
        // For raw markdown fallback, use a pre element with proper styling
        return html`<pre style="white-space: pre-wrap; font-family: var(--font-family); line-height: var(--leading-relaxed);">${DOMPurify.sanitize(this.markdown, { ALLOWED_ATTR: [], ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.-]|$))/i })}</pre>`;
      }
    } else {
      return html``;
    }
  }
}

// Register the custom element
customElements.define("markdown-element", MarkdownElement);